import { getState, resetState, setState } from '../store';
import { navigate } from '../router';
import { vibrateSuccess } from '../utils/haptic';
import { escapeHtml } from '../utils/escape';
import { formatMmSs, computeBreakdown } from '../utils/time-format';
import { clearAllGameData } from '../utils/state-persistence';
import { getPlayerRecap, type PlayerRecapData, type RecapLocationData } from '../api/game-api';
import { createViewLifecycle, type ViewLifecycle } from '../utils/view-lifecycle';

/**
 * Session 71 — Mission Debrief boot sequence.
 *
 * Per DESIGN.md Motion §: animations serve function, never decoration.
 * This results view replaces the static «КВЕСТ ПРОЙДЕН» card with a
 * cockpit-HUD boot sequence: horizon scan, bracket lock-in around the
 * trophy, monospace timer count-up, staggered data rows, and finally
 * the action buttons. Total ~1.4s. Tappable to skip at any point.
 *
 * The "cinematic recap" feature request (ken-burns + stars + horizon
 * + html2canvas share) was reframed during design-review — the original
 * social-app idiom violates DESIGN.md anti-patterns (decorative parallax,
 * decorative blobs, gradient hero). Mission Debrief keeps the emotional
 * payoff without breaking the Mission Control aesthetic. Telemetry
 * timeline (per-location bars) and share card are future ships.
 */

/**
 * Plan B Task 12 (2026-05-11) — minimal `ViewLifecycle` wiring for results-view.
 * The view doesn't have a visibility-reconcile pattern (no WS subscriptions,
 * no live polling — once mounted, results are static), but it DOES need an
 * `lc` to thread through `openShareCardPreview` so the share-card modal
 * tears down with the view via `bodyOverlays.register('share-card', ..., lc)`.
 *
 * Pre-Plan-B v0.80.9 used a module-level `activeShareModalDismiss` slot plus
 * a defensive `document.getElementById('aq-share-modal')?.remove()` fallback
 * inside `destroyResultsView` to handle the await-window race (modal DOM
 * appended synchronously, but the dismiss fn was returned only after the
 * server fetch resolved, so a navigate-away mid-fetch left the slot null).
 * `bodyOverlays.register(...)` runs synchronously before the await now, so
 * the lc-cleanup path covers the same race without the manual sweep.
 */
let activeLc: ViewLifecycle | null = null;

/** Total boot duration in ms — also the time after which `complete()` fires. */
const BOOT_DURATION_MS = 1400;

/** Per-row animation stagger (T=800ms onward). */
const ROW_DELAYS_MS = [800, 880, 960, 1040, 1120];
const ACTIONS_DELAY_MS = 1200;

/** Count-up tween for the timer readout (T=400..800ms). Linear so the
 *  final value lands exactly on the displayed totalTimeMs. */
function animateCountUp(
  el: HTMLElement,
  fromMs: number,
  toMs: number,
  durationMs: number,
  formatter: (ms: number) => string,
  onDone?: () => void,
): () => void {
  const startPerf = performance.now();
  let rafId = 0;
  let cancelled = false;

  function step(now: number): void {
    if (cancelled) return;
    const elapsed = now - startPerf;
    const t = Math.min(1, elapsed / durationMs);
    const currentMs = Math.round(fromMs + (toMs - fromMs) * t);
    el.textContent = formatter(currentMs);
    if (t < 1) {
      rafId = requestAnimationFrame(step);
    } else if (onDone) {
      onDone();
    }
  }
  rafId = requestAnimationFrame(step);

  return () => {
    cancelled = true;
    if (rafId) cancelAnimationFrame(rafId);
    el.textContent = formatter(toMs);
  };
}

export function renderResultsView(container: HTMLElement): void {
  const state = getState();
  if (!state.gameCode) { navigate('join'); return; }
  vibrateSuccess();

  // Plan B Task 12 — minimal lc so openShareCardPreview can register its
  // teardown via bodyOverlays. No WS / no polling here, so no
  // installVisibilityReconcile and no setInterval registrations.
  activeLc = createViewLifecycle();

  // Split base time from penalty so we can show the breakdown. state.totalTimeMs
  // is the locally-tracked total (set at quest end), penaltyMs is the sidecar.
  // If penaltyMs > 0 we display base + penalty rows separately.
  const breakdown = computeBreakdown(state.totalTimeMs, state.penaltyMs);
  const penaltyMs = breakdown.penaltyMs;
  const timeStr = formatMmSs(breakdown.totalMs);
  const baseStr = formatMmSs(breakdown.baseMs);
  const penaltyStr = formatMmSs(breakdown.penaltyMs);

  // Row delays map 1:1 to ROW_DELAYS_MS. Rows that don't render (no
  // penalty branch) simply skip their delay slot.
  const rowDelay = (idx: number): number => ROW_DELAYS_MS[idx] ?? ROW_DELAYS_MS[ROW_DELAYS_MS.length - 1];

  container.innerHTML = `
    <div class="screen" style="justify-content: center; align-items: center; position: relative;" data-testid="results-screen">
      <div class="recap-horizon-scan" aria-hidden="true"></div>

      <div style="text-align: center; max-width: 400px; width: 100%;">
        <p class="text-eyebrow recap-fade-in" style="--recap-delay: 150ms; margin-bottom: var(--sp-sm);" data-testid="recap-eyebrow">
          МИССИЯ ЗАВЕРШЕНА
        </p>

        <div style="display: flex; justify-content: center; align-items: center; gap: var(--sp-sm); margin-bottom: var(--sp-lg);">
          <span class="recap-bracket recap-bracket--left" aria-hidden="true">[</span>
          <div class="trophy-enter" style="font-size: 72px; line-height: 1;" data-testid="recap-trophy">🏆</div>
          <span class="recap-bracket recap-bracket--right" aria-hidden="true">]</span>
        </div>

        <h1 class="text-display-large recap-fade-in" style="--recap-delay: 550ms; color: var(--amber); margin-bottom: var(--sp-sm);">
          КВЕСТ ПРОЙДЕН!
        </h1>
        <p class="text-body recap-fade-in" data-testid="recap-hero-team" style="--recap-delay: 650ms; color: var(--text-secondary); margin-bottom: var(--sp-xl);">
          ${(state.teamName || state.playerName).startsWith('__DEMO__') ? 'Демо-прогон' : escapeHtml(state.teamName || state.playerName)}
        </p>

        <div class="card recap-fade-in" style="--recap-delay: ${rowDelay(0)}ms; text-align: left; margin-bottom: var(--sp-xl);">
          <p class="text-eyebrow" style="margin-bottom: var(--sp-md);">ИТОГ</p>

          <div style="display: flex; justify-content: space-between; margin-bottom: var(--sp-sm);">
            <span class="text-body" style="color: var(--text-secondary);">Точки</span>
            <span class="text-data" style="color: var(--success);">${state.currentLocationIndex} / ${state.locations.length}</span>
          </div>

          ${penaltyMs > 0 ? `
          <div style="display: flex; justify-content: space-between; margin-bottom: var(--sp-sm);">
            <span class="text-body" style="color: var(--text-secondary);">Базовое время</span>
            <span class="text-data" data-testid="recap-time-base">${baseStr}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: var(--sp-sm);">
            <span class="text-body" style="color: var(--text-secondary);">Штраф за пропуски</span>
            <span class="text-data" style="color: var(--danger);">+${penaltyStr}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: var(--sp-sm); padding-top: var(--sp-sm); border-top: 1px solid var(--bg-border);">
            <span class="text-body" style="color: var(--text-secondary);">Итого</span>
            <span class="text-data" style="font-weight: 700;" data-testid="recap-time-total">${timeStr}</span>
          </div>
          ` : `
          <div style="display: flex; justify-content: space-between; margin-bottom: var(--sp-sm);">
            <span class="text-body" style="color: var(--text-secondary);">Время</span>
            <span class="text-data" data-testid="recap-time-total">${timeStr}</span>
          </div>
          `}

          <div style="display: flex; justify-content: space-between;">
            <span class="text-body" style="color: var(--text-secondary);">Неверных ответов</span>
            <span class="text-data" data-testid="recap-wrongs" style="color: ${state.wrongAnswers > 0 ? 'var(--danger)' : 'var(--success)'};">${state.wrongAnswers}</span>
          </div>
        </div>

        <details class="recap-telemetry recap-fade-in" style="--recap-delay: 1080ms;" data-testid="recap-telemetry">
          <summary>ТЕЛЕМЕТРИЯ</summary>
          <div class="recap-telemetry-body" data-testid="recap-telemetry-body">
            <p class="text-body" style="color: var(--text-secondary); text-align: center; margin: 0;">Загрузка...</p>
          </div>
        </details>

        <div class="recap-fade-in" style="--recap-delay: ${ACTIONS_DELAY_MS}ms; display: flex; flex-direction: column; gap: var(--sp-md);">
          <button id="results-leaderboard" class="btn btn-primary btn-full">ЛИДЕРБОРД</button>
          ${state.allowTeamChat ? `<button id="results-chat" class="btn btn-secondary btn-full">ЧАТ С КОМАНДОЙ</button>` : ''}
          <button id="results-share" class="btn btn-secondary btn-full">ПОДЕЛИТЬСЯ</button>
          <button id="results-new" class="btn btn-secondary btn-full">НОВАЯ ИГРА</button>
        </div>
      </div>
    </div>
  `;

  // Count-up tween for the primary time readout. Runs in parallel with
  // the CSS fade-in (the element fades in at T=800ms, count-up runs
  // T=400..800ms so the number is already at `timeStr` when it becomes
  // visible — this gives the fade-in a "locked" feel instead of a
  // distracting counting animation the user might read mid-tween).
  //
  // Tween duration deliberately ends BEFORE visual reveal so the motion
  // is felt subconsciously (instrument-panel boot) without the user
  // staring at flipping digits.
  const totalEl = container.querySelector<HTMLElement>('[data-testid="recap-time-total"]');
  let cancelCountUp: (() => void) | null = null;
  if (totalEl && !prefersReducedMotion()) {
    cancelCountUp = animateCountUp(
      totalEl,
      0,
      breakdown.totalMs,
      400,
      formatMmSs,
    );
  }

  // Tap-to-skip: any pointer interaction on the screen before T=1400ms
  // fast-forwards all animations to their final state. After T=1400ms
  // the button click handlers take over naturally.
  const screenEl = container.querySelector<HTMLElement>('[data-testid="results-screen"]');
  let skipped = false;
  const skipBootSequence = (): void => {
    if (skipped) return;
    skipped = true;
    if (cancelCountUp) cancelCountUp();
    screenEl?.classList.add('recap-skip');
  };

  // Listen for pointer-down so even a partial swipe/tap triggers skip.
  // Don't skip on button clicks — the click handlers need to still fire,
  // and by T=1200ms the buttons are already fading in; user clicking
  // "ЛИДЕРБОРД" means they're ready to move on, not that they want to
  // interrupt their OWN action.
  screenEl?.addEventListener('pointerdown', (e) => {
    if (e.target instanceof Element && e.target.closest('button')) return;
    skipBootSequence();
  }, { once: true });

  // Auto-clear skip guard after boot finishes so re-entering the view
  // (unlikely but possible) starts fresh.
  setTimeout(skipBootSequence, BOOT_DURATION_MS);

  // Session 71 — Telemetry timeline (Ship 2/3). Originally lazy-fetched on
  // first open. /investigate 2026-04-27 prod 125908 changed this: we now
  // eager-fetch on mount so the team-scoped `teamWrongAnswers` total can
  // overwrite the wrong local `state.wrongAnswers` in the summary card
  // (Capt A saw 1, Capt B saw 0 for the same team — local count is
  // per-player, not team). Telemetry render still happens on user expand;
  // the eager fetch primes the cache so expand is instant.
  const telemetryDetails = container.querySelector<HTMLDetailsElement>('[data-testid="recap-telemetry"]');
  const telemetryBody = container.querySelector<HTMLElement>('[data-testid="recap-telemetry-body"]');
  const wrongsEl = container.querySelector<HTMLElement>('[data-testid="recap-wrongs"]');
  let telemetryRendered = false;

  function patchWrongsFromRecap(recap: PlayerRecapData): void {
    if (!wrongsEl) return;
    if (typeof recap.teamWrongAnswers !== 'number') return;
    if (recap.teamWrongAnswers === getState().wrongAnswers) return;
    setState({ wrongAnswers: recap.teamWrongAnswers });
    wrongsEl.textContent = String(recap.teamWrongAnswers);
    wrongsEl.style.color = recap.teamWrongAnswers > 0 ? 'var(--danger)' : 'var(--success)';
  }

  let recapPromise: Promise<PlayerRecapData> | null = null;

  if (telemetryDetails && telemetryBody) {
    if (state.fullOffline) {
      // fullOffline games have no server to ask — hide the details block,
      // skip the eager wrongs sync (PWA owns the count locally anyway).
      telemetryDetails.style.display = 'none';
    } else {
      // Eager fetch on mount — primes cache + corrects wrongs ASAP.
      // Don't block the boot animation; just patch the span when ready.
      // recapPromise is shared with the toggle handler so a user expanding
      // telemetry while the fetch is in flight reuses it instead of
      // double-fetching. Cached on success → next toggles render instantly.
      recapPromise = getPlayerRecap(state.gameCode, state.playerId, state.playerToken)
        .then((recap) => {
          patchWrongsFromRecap(recap);
          return recap;
        })
        .catch((err) => {
          console.warn('[results-view] eager recap fetch failed', err);
          throw err;
        });
      // Swallow the unhandled-rejection if telemetry never opens. The
      // toggle handler owns the user-visible error path.
      recapPromise.catch(() => { /* toggle path handles UX */ });

      telemetryDetails.addEventListener('toggle', async () => {
        if (!telemetryDetails.open || telemetryRendered) return;
        telemetryRendered = true;
        try {
          const recap = await recapPromise;
          if (recap) renderTelemetryInto(telemetryBody, recap);
        } catch {
          // Eager failed → user-driven retry path. Reset the guard so a
          // collapse + reopen triggers a fresh fetch.
          telemetryRendered = false;
          loadAndRenderTelemetry(telemetryBody, state.gameCode, state.playerId, state.playerToken)
            .then((recap) => {
              patchWrongsFromRecap(recap);
              telemetryRendered = true;
            })
            .catch(() => { telemetryRendered = false; });
        }
      });
    }
  }

  container.querySelector('#results-leaderboard')!.addEventListener('click', () => {
    navigate('leaderboard');
  });

  // Session 71 Ship 3/3 — Mission Debrief share card. Replaces the
  // text-only navigator.share with a preview modal showing a generated
  // 1080×1920 PNG stamp in Mission Control HUD aesthetic. Dynamically
  // imports the share-card module so html2canvas (~150 KB) stays out
  // of the main bundle until the user actually taps ПОДЕЛИТЬСЯ.
  //
  // Session 71 /review fix — disable button during render to block
  // double-tap race. iOS misidentifies a scroll-settle as a tap → two
  // parallel html2canvas renders stacked two overlays, each with its
  // own blob URL and offscreen 1080×1920 DOM tree. Under memory
  // pressure mobile Safari crashed the tab. Single button click now
  // takes the button out of service until the modal is dismissed.
  const shareBtn = container.querySelector<HTMLButtonElement>('#results-share')!;
  shareBtn.addEventListener('click', async () => {
    if (shareBtn.disabled) return;
    shareBtn.disabled = true;
    try {
      const { openShareCardPreview } = await import('../utils/share-card');
      // Plan B Task 12 — bodyOverlays.register('share-card', close, lc) inside
      // openShareCardPreview runs SYNCHRONOUSLY (before the server-fetch await),
      // so a navigate-away mid-fetch is swept by activeLc.cleanup() in
      // destroyResultsView via the lc.onDestroy hook. No more module-level
      // dismiss slot, no defensive DOM-query fallback.
      // Session 72 — server-fetched + HMAC-signed PNG. Numeric figures
      // flow ONLY into the share-sheet text caption; the rendered image is
      // server-of-record. Auth fields drive the request.
      await openShareCardPreview(document.body, {
        gameCode: state.gameCode,
        playerId: state.playerId,
        playerToken: state.playerToken,
        completedAt: state.completedAt || Date.now(),
        totalTimeMs: state.totalTimeMs,
        locationsCompleted: state.currentLocationIndex,
        totalLocations: state.locations.length,
      }, activeLc!);
    } catch (err) {
      console.warn('[results-view] share-card load failed', err);
      // Fallback: old text-only share if the dynamic import fails
      // (e.g. service worker serving stale bundle on an offline client).
      const shareData = {
        title: 'AutoQuest',
        text: `Квест пройден! Время: ${timeStr}, точки: ${state.currentLocationIndex}/${state.locations.length}`,
      };
      if (navigator.share) {
        try { await navigator.share(shareData); } catch { /* cancelled */ }
      } else {
        try {
          await navigator.clipboard.writeText(shareData.text);
          alert('Скопировано в буфер обмена');
        } catch { /* ignore */ }
      }
    } finally {
      // Re-enable after modal resolves (either success-mount or error-
      // fallback path). MutationObserver for overlay-detached would be
      // tighter, but the await flow already waits until the modal is
      // mounted + buttons enabled; a user dismissing the modal manually
      // then clicking ПОДЕЛИТЬСЯ again gets a fresh modal, not a stack.
      shareBtn.disabled = false;
    }
  });

  container.querySelector('#results-new')!.addEventListener('click', () => {
    // Session 69 — clear both in-memory state AND ALL persisted PWA data
    // (game-state blob + error log + stale demo-join). `resetState` alone
    // only touches `autoquest_game_state`; `aq_errors` / `aq_demo_join`
    // would survive and leak previous-session detail into fresh play.
    clearAllGameData();
    resetState();
    navigate('join');
  });

  // 2026-04-26 user request — when team chat is on, give the team a way
  // back into chat from the results screen (debrief / coordinate the next
  // round / share photos). Button rendered conditionally (line 152) only
  // when state.allowTeamChat is true, so this querySelector returns null
  // for chat-off games — guard with optional chaining.
  container.querySelector('#results-chat')?.addEventListener('click', () => {
    navigate('chat');
  });
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Session 71 — render telemetry timeline (Ship 2/3). Fetches per-location
 * timings from the backend and renders one animated bar per completed
 * location. Stagger via `--recap-delay` CSS var (100 ms step). Bar width
 * proportional to timeMs / maxTimeMs, so the longest location reads at
 * 100% and short ones scale relative — gives instant visual rhythm
 * without forcing the user to read timestamps.
 *
 * Never fails loudly. Any HTTP / network error just renders a dim «—»
 * message; the player still has their ИТОГ card above and the actions
 * below. Results-view works offline-ish by design.
 */
async function loadAndRenderTelemetry(
  host: HTMLElement,
  gameCode: string,
  playerId: string,
  playerToken: string,
): Promise<PlayerRecapData> {
  try {
    const recap = await getPlayerRecap(gameCode, playerId, playerToken);
    renderTelemetryInto(host, recap);
    return recap;
  } catch (err) {
    console.warn('[results-view] telemetry fetch failed', err);
    host.innerHTML = `<p class="text-body" style="color: var(--text-hint); text-align: center; margin: 0;">Данные недоступны.</p>`;
    // Rethrow so the caller can reset the guard and let the user retry by
    // collapsing + reopening the details block. The error message is
    // already rendered for them, rethrow is purely a control-flow signal.
    throw err;
  }
}

function renderTelemetryInto(host: HTMLElement, recap: PlayerRecapData): void {
  if (recap.locations.length === 0) {
    host.innerHTML = `<p class="text-body" style="color: var(--text-hint); text-align: center; margin: 0;">Нет завершённых точек.</p>`;
    return;
  }
  // Scale bars relative to the longest location — pragmatic visual
  // hierarchy: longest run reads as «the wall», shorter ones read as
  // «the sprint». Absolute durations still carry in the timestamp text.
  const maxTimeMs = recap.locations.reduce((m, l) => Math.max(m, l.timeMs), 1);
  const rows = recap.locations.map((loc, i) => renderTelemetryRow(loc, i, maxTimeMs)).join('');
  host.innerHTML = rows;
}

function renderTelemetryRow(loc: RecapLocationData, idx: number, maxTimeMs: number): string {
  // Width percentage — max location gets 100%, others proportional.
  // Floor at 8% so a near-zero location (instant auto-advance) still
  // renders a visible bar sliver, not disappearing.
  const pct = Math.max(8, Math.round((loc.timeMs / maxTimeMs) * 100));
  const fillVariant =
    loc.type === 'timeout' ? 'recap-telemetry-row__fill--timeout' :
    loc.type === 'skip'    ? 'recap-telemetry-row__fill--skip'    :
    loc.type === 'org'     ? 'recap-telemetry-row__fill--org'     : '';
  // Delay per-row stagger: 100 ms step, matches DESIGN.md motion rhythm
  // (fits within short(200ms) scale). On details toggle-open this
  // produces a sequential left-to-right «telemetry materialization».
  const delayMs = idx * 100;
  const label = `ТОЧКА ${escapeHtml(String(loc.index + 1))}`;
  const timeLabel = formatMmSs(loc.timeMs);
  const wrongsBadge = loc.wrongAnswers > 0
    ? `<span class="recap-telemetry-row__wrongs" aria-label="${loc.wrongAnswers} неверных ответов">×${loc.wrongAnswers}</span>`
    : '';
  // wrongsBadge is sibling-absolute inside the bar; stays legible even
  // on the dashed-skip variant because text sits above the border line.
  return `
    <div class="recap-telemetry-row" role="listitem">
      <span class="recap-telemetry-row__label">${label}</span>
      <div class="recap-telemetry-row__bar" role="img" aria-label="Время на точке ${loc.index + 1}: ${timeLabel}${loc.wrongAnswers > 0 ? ', ' + loc.wrongAnswers + ' неверных' : ''}, тип: ${loc.type}">
        <span class="recap-telemetry-row__fill ${fillVariant}" style="--recap-fill-width: ${pct}%; --recap-delay: ${delayMs}ms;"></span>
        ${wrongsBadge}
      </div>
      <span class="recap-telemetry-row__time">${timeLabel}</span>
    </div>
  `;
}

/**
 * v0.80.9 (2026-05-04) — view-destroy hook for results-view, wired into
 * `setView` from main.ts. Pre-fix results-view had NO destroy hook at all,
 * so the share-card modal (a body-attached fixed overlay) could persist
 * if the user navigated away with the modal open. Same global-singleton
 * class as the connection-bar bug closed in v0.80.7.
 *
 * Plan B Task 12 (2026-05-11) — replaced the original two-layer slot+fallback
 * sweep with a single `activeLc?.cleanup()`. The share-card modal registers
 * its dismiss closure via `bodyOverlays.register('share-card', close, lc)`
 * BEFORE the server fetch await, so the lc.onDestroy hook is attached
 * synchronously. The await-window race the v0.80.9 fallback covered is now
 * closed by the registry side; no DOM-query fallback needed.
 */
export function destroyResultsView(): void {
  activeLc?.cleanup();
  activeLc = null;
}
